/**
 * The history's words on the screen — the frontend half of
 * `tests/fixtures/credential-history-vocabulary.json`.
 *
 * The backend holds the same table (`domain/history/vocabulary.ts`) because the CSV download has to
 * spell a status exactly as the screen it came from does. Neither imports the other — two packages,
 * two builds — so each has its own test that compares it with the canonical file field by field.
 *
 * `caution` is the part that does the work. Every name is the level the act was observed at, and
 * where a name could still be read as a completion, the caution says what is missing: a print
 * window is not a printed document, a download request is not a saved file, a redirect is not a
 * profile entry, a delivered e-mail is not a read one.
 */

export interface HistoryWord {
  tr: string;
  caution: string | null;
}

export const HISTORY_EVENTS: Record<string, HistoryWord> = {
  created: { tr: 'Kayıt oluşturuldu', caution: null },
  accepted: { tr: 'Kayıt sahibi kabul etti', caution: null },
  declined: { tr: 'Kayıt sahibi reddetti', caution: null },
  published: { tr: 'Yayımlandı', caution: null },
  revoked: { tr: 'İptal edildi', caution: null },
  reissued: { tr: 'Yeniden düzenlendi', caution: null },
  superseded: { tr: 'Yerine yeni kayıt geçti', caution: null },
  corrected: { tr: 'Düzeltildi', caution: null },
  credential_corrected: { tr: 'Düzeltildi', caution: null },
  credential_admin_published: { tr: 'Yönetici onayıyla yayımlandı', caution: null },
  email_queued: { tr: 'E-posta kuyruğa alındı', caution: null },
  email_sent: {
    tr: 'E-posta sağlayıcıya verildi',
    caution: 'Sağlayıcıya teslim edilmesi, alıcının okuduğu anlamına gelmez.',
  },
  email_failed: { tr: 'E-posta gönderilemedi', caution: null },
  token_rotated: { tr: 'Kabul bağlantısı yenilendi', caution: null },
  linkedin_member_authorized: {
    tr: 'LinkedIn hesabı bağlandı',
    caution: 'Hesabın bağlanması, bir gönderi oluşturulduğu anlamına gelmez.',
  },
  linkedin_api_sync_requested: { tr: 'LinkedIn eşitlemesi istendi', caution: null },
  linkedin_api_sync_simulated: { tr: 'LinkedIn eşitlemesi simüle edildi', caution: null },
  import_committed: { tr: 'Toplu içe aktarım işlendi', caution: null },
  import_rolled_back: { tr: 'Toplu içe aktarım geri alındı', caution: null },
  persistence_degraded: { tr: 'Kalıcılık bozulması kaydedildi', caution: null },
  role_assigned: { tr: 'Rol atandı', caution: null },
  role_revoked: { tr: 'Rol geri alındı', caution: null },
  authorization_denied: { tr: 'Yetki reddedildi', caution: null },
  asset_created: { tr: 'Görsel yüklendi', caution: null },
  font_created: { tr: 'Font yüklendi', caution: null },
  template_published: { tr: 'Şablon sürümü yayımlandı', caution: null },
  template_archived: { tr: 'Şablon arşivlendi', caution: null },
  batch_created: { tr: 'Toplu yükleme oluşturuldu', caution: null },
  batch_committed: { tr: 'Toplu yükleme işlendi', caution: null },
  user_activated: { tr: 'Kullanıcı aktifleştirildi', caution: null },
  email_handed_to_provider: {
    tr: 'E-posta sağlayıcıya verildi',
    caution: 'Sağlayıcıya teslim edilmesi, alıcının okuduğu anlamına gelmez.',
  },
  email_delivered: {
    tr: 'Sağlayıcı teslim etti',
    caution: 'Teslim, alıcının e-postayı açtığı veya okuduğu anlamına gelmez.',
  },
  email_bounced: { tr: 'E-posta geri döndü', caution: null },
  email_suppressed: { tr: 'Adres engelli olduğu için gönderilmedi', caution: null },
  public_viewed: {
    tr: 'Doğrulama sayfası görüntülendi',
    caution: 'Ziyaretçinin kimliği doğrulanmadı; bu istek kayıt sahibine ait olmayabilir.',
  },
  claim_opened: { tr: 'Kabul sayfası açıldı', caution: 'Ziyaretçinin kimliği doğrulanmadı.' },
  downloaded: { tr: 'İndirme isteği', caution: 'Dosyanın cihaza kaydedildiği doğrulanmadı.' },
  printed: { tr: 'Yazdırma penceresi açıldı', caution: 'Belgenin yazdırıldığı doğrulanmadı.' },
  linkedin_helper_opened: {
    tr: 'LinkedIn profil ekleme başlatıldı',
    caution: "LinkedIn'e yönlendirme, profil sertifikasının kaydedildiğini kanıtlamaz.",
  },
  linkedin_share_opened: {
    tr: 'Paylaşım penceresi açıldı',
    caution: 'Pencerenin açılması, bir gönderi oluşturulduğu anlamına gelmez.',
  },
  og_preview_opened: { tr: 'Paylaşım önizlemesi açıldı', caution: null },
  link_copied: {
    tr: 'Doğrulama bağlantısı kopyalandı',
    caution: 'Bağlantının nerede kullanıldığı ölçülmez.',
  },
  text_copied: { tr: 'Paylaşım metni kopyalandı', caution: 'Metnin nerede kullanıldığı ölçülmez.' },
  linkedin_profile_add_started: {
    tr: 'LinkedIn profil ekleme başlatıldı',
    caution: "LinkedIn'e yönlendirme, profil sertifikasının kaydedildiğini kanıtlamaz.",
  },
  linkedin_share_started: {
    tr: 'LinkedIn paylaşımı başlatıldı',
    caution: 'Başlatılması, gönderinin oluşturulduğu anlamına gelmez.',
  },
  linkedin_share_published: { tr: "LinkedIn'de paylaşıldı", caution: null },
  linkedin_share_result_unknown: {
    tr: 'LinkedIn paylaşımının sonucu belirlenemedi',
    caution: 'Gönderi oluşmuş da olabilir, oluşmamış da; sunucu ayırt edemedi.',
  },
  linkedin_connect_succeeded: {
    tr: 'LinkedIn hesabı bağlandı',
    caution: 'Hesabın bağlanması, bir gönderi oluşturulduğu anlamına gelmez.',
  },
  linkedin_connect_failed: { tr: 'LinkedIn hesabı bağlanamadı', caution: null },
};

export const HISTORY_CATEGORY_TR: Record<string, string> = {
  system: 'Sistem işlemleri',
  interaction: 'Kullanıcı etkileşimleri',
};

export const HISTORY_SOURCE_TR: Record<string, string> = {
  audit_ledger: 'Denetim defteri',
  email_delivery: 'E-posta gönderimi',
  usage_measurement: 'Kullanım ölçümü',
  linkedin_engagement: 'LinkedIn etkileşim kaydı',
};

export const HISTORY_EVIDENCE_TR: Record<string, string> = {
  system_recorded: 'Sistem kaydı',
  provider_reported: 'Sağlayıcı bildirimi',
  server_verified: 'Sunucu doğrulaması',
  client_reported: 'Tarayıcı bildirimi',
};

export const HISTORY_OUTCOME_TR: Record<string, string> = {
  recorded: 'Kaydedildi',
  succeeded: 'Başarılı',
  failed: 'Başarısız',
  unknown: 'Sonuç bilinmiyor',
};

export const HISTORY_ACTOR_TR: Record<string, string> = {
  user: 'Yönetici hesabı',
  system: 'Sistem',
  recipient: 'Kayıt sahibi',
  anonymous: 'Anonim ziyaretçi',
};

/** Shown where a line has an actor the store could not establish at all. */
export const HISTORY_ACTOR_UNVERIFIED = 'Kimliği doğrulanmadı';

/** «Bu oturumda kayıt yok» is gone: the history is durable, so an empty one is a fact about it. */
export const HISTORY_EMPTY = 'Henüz kaydedilmiş hareket yok';

/** An identifier nobody has named is shown as itself rather than guessed at. */
export function historyLabel(event: string): string {
  return HISTORY_EVENTS[event]?.tr ?? event;
}

/** What the line does NOT prove, or nothing where there is nothing to warn about. */
export function historyCaution(event: string): string | null {
  return HISTORY_EVENTS[event]?.caution ?? null;
}
